import React, { useMemo } from "react";

import {
  differenceInCalendarDays,
  format,
  isValid,
  parseISO,
  subDays,
} from "date-fns";
import Chart from "react-apexcharts";
import { useTranslation } from "react-i18next";

import FinanceChartEmpty from "./FinanceChartEmpty";
import FinanceTrendSubtitle from "./FinanceTrendSubtitle";
import CfLoader from "../../common/CfLoader/CfLoader";
import { cssVar, DASHBOARD_CHART_CSS_VARS } from "../../utils/cssVariables";
import { getDateFnsLocale } from "../../utils/getDateFnsLocale";

const CHART_HEIGHT = 320;

const WIDE_SERIES_THRESHOLD = 10;

export const TREND_GRANULARITIES = ["day", "week", "month"];

const toDate = (value) => {
  if (!value) return null;
  const parsed = parseISO(value);
  return isValid(parsed) ? parsed : null;
};

const resolveGranularity = (granularity, points) => {
  if (TREND_GRANULARITIES.includes(granularity)) return granularity;

  const first = toDate(points[0]?.period_start);
  const second = toDate(points[1]?.period_start);
  if (!first || !second) {
    const end = toDate(points[0]?.period_end);
    if (!first || !end) return "month";
    const span = differenceInCalendarDays(end, first);
    if (span <= 1) return "day";
    return span <= 7 ? "week" : "month";
  }

  const gap = differenceInCalendarDays(second, first);
  if (gap <= 1) return "day";
  return gap <= 7 ? "week" : "month";
};

const BillingTrendChart = ({
  points = [],
  granularity,
  comparison = null,
  loading = false,
}) => {
  const { t, i18n } = useTranslation();
  const dateLocale = useMemo(
    () => getDateFnsLocale(i18n.language),
    [i18n.language],
  );

  const resolvedGranularity = useMemo(
    () => resolveGranularity(granularity, points),
    [granularity, points],
  );

  const spansMultipleYears = useMemo(() => {
    const years = new Set();
    points.forEach((point) => {
      const date = toDate(point.period_start);
      if (date) years.add(date.getFullYear());
    });
    return years.size > 1;
  }, [points]);

  const axisLabelFormat = useMemo(() => {
    if (resolvedGranularity === "month") {
      return spansMultipleYears ? "MMM yy" : "MMM";
    }
    return spansMultipleYears ? "d MMM yy" : "d MMM";
  }, [resolvedGranularity, spansMultipleYears]);

  const categories = useMemo(
    () =>
      points.map((point) => {
        const date = toDate(point.period_start);
        if (!date) return point.period_start ?? "";
        return format(date, axisLabelFormat, { locale: dateLocale });
      }),
    [points, axisLabelFormat, dateLocale],
  );

  const tooltipLabels = useMemo(
    () =>
      points.map((point) => {
        const start = toDate(point.period_start);
        if (!start) return point.period_start ?? "";

        if (resolvedGranularity === "month") {
          return format(start, "MMMM yyyy", { locale: dateLocale });
        }
        if (resolvedGranularity === "day") {
          return format(start, "d MMM yyyy", { locale: dateLocale });
        }

        const end = toDate(point.period_end);
        if (!end) return format(start, "d MMM yyyy", { locale: dateLocale });
        const lastDay = subDays(end, 1);
        return `${format(start, "d MMM", { locale: dateLocale })} – ${format(
          lastDay,
          "d MMM yyyy",
          { locale: dateLocale },
        )}`;
      }),
    [points, resolvedGranularity, dateLocale],
  );

  const seriesData = useMemo(
    () => points.map((point) => Number(point.credentials_issued) || 0),
    [points],
  );

  const hasData = seriesData.some((value) => value > 0);

  const options = useMemo(
    () => ({
      chart: {
        type: "bar",
        toolbar: { show: false },
        zoom: { enabled: false, allowMouseWheelZoom: false },
        fontFamily: "Inter, sans-serif",
      },
      tooltip: {
        theme: "dark",
        x: {
          formatter: (_value, { dataPointIndex }) =>
            tooltipLabels[dataPointIndex] ?? "",
        },
      },
      xaxis: {
        categories,
        tickPlacement: "on",
        labels: {
          hideOverlappingLabels: true,
          rotate: -45,
          rotateAlways: false,
          style: {
            colors: cssVar(DASHBOARD_CHART_CSS_VARS.axisLabel),
            fontSize: "13px",
            fontWeight: "500",
          },
        },
      },
      yaxis: {
        labels: {
          style: {
            colors: cssVar(DASHBOARD_CHART_CSS_VARS.gridLabel),
            fontSize: "13px",
          },
        },
      },
      grid: {
        strokeDashArray: 5,
      },
      fill: {
        type: "gradient",
        gradient: {
          type: "vertical",
          shadeIntensity: 1,
          opacityFrom: 0.7,
          opacityTo: 0.9,
          colorStops: [
            [
              {
                offset: 0,
                color: cssVar(DASHBOARD_CHART_CSS_VARS.barGradientStart),
                opacity: 1,
              },
              {
                offset: 100,
                color: cssVar(DASHBOARD_CHART_CSS_VARS.barGradientEnd),
                opacity: 0.28,
              },
            ],
          ],
        },
      },
      dataLabels: { enabled: false },
      plotOptions: {
        bar: {
          borderRadius: 4,
          columnWidth:
            categories.length > WIDE_SERIES_THRESHOLD ? "60%" : "28px",
        },
      },
    }),
    [categories, tooltipLabels],
  );

  const series = useMemo(
    () => [
      {
        name: t("finance:credentials_issued"),
        data: seriesData,
      },
    ],
    [seriesData, t],
  );

  return (
    <div className="bg-white border border-gray-200 rounded-2xl shadow-xl p-6 h-full">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-800">
          {t("finance:issuance_trend_title")}
        </h3>
        <FinanceTrendSubtitle
          comparison={loading ? null : comparison}
          countLabelKey="finance:issuance_trend_delta_count"
          subtitle={t("finance:issuance_trend_subtitle")}
          testId="billing-trend"
        />
      </div>
      {loading ? (
        <div
          className="flex items-center justify-center"
          style={{ height: CHART_HEIGHT }}
        >
          <CfLoader />
        </div>
      ) : !hasData ? (
        <FinanceChartEmpty height={CHART_HEIGHT} />
      ) : (
        <Chart
          options={options}
          series={series}
          type="bar"
          height={CHART_HEIGHT}
        />
      )}
    </div>
  );
};

export default BillingTrendChart;
